/**
 * ManagerSelect — Searchable dropdown for Direct/Approving Manager selection.
 *
 * Replaces the native shadcn `<Select>` which has no search input. Built on top
 * of the `Command` + `Popover` shadcn primitives. Filters across English first +
 * last name, Arabic first + last name, department, and job title. Supports an
 * explicit "None" option and RTL.
 *
 * Props
 *  - value          string | ''            currently selected employee id
 *  - onChange       (id: string) => void   emit '' for "none"
 *  - employees      Employee[]             options to render
 *  - loading        boolean                disables trigger while loading
 *  - placeholder    string                 button placeholder
 *  - noneLabel      string                 label for the "none" option
 *  - searchPlaceholder string              search field placeholder
 *  - emptyText      string                 shown when no results
 *  - testId         string                 base data-testid (suffixed with -trigger/-search)
 */
import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export function ManagerSelect({
  value,
  onChange,
  employees = [],
  loading = false,
  placeholder,
  noneLabel,
  searchPlaceholder,
  emptyText,
  testId = 'manager-select',
  disabled = false,
  fallbackEmployee = null,    // optional: shown in trigger if value is set but not in `employees`
}) {
  const { isRTL } = useLanguage();
  const [open, setOpen] = useState(false);

  // Sort employees alphabetically by English name (the most common scan)
  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const an = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
      const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
      return an.localeCompare(bn);
    });
  }, [employees]);

  // Currently selected employee — search the filtered pool first, then fall back
  // to the explicit fallbackEmployee so legacy assignments still render their name.
  const selected = useMemo(() => {
    const inPool = sortedEmployees.find((e) => e.id === value);
    if (inPool) return inPool;
    if (fallbackEmployee && fallbackEmployee.id === value) return fallbackEmployee;
    return null;
  }, [sortedEmployees, value, fallbackEmployee]);

  // Display label inside the trigger
  const triggerLabel = (() => {
    if (!value || !selected) {
      return placeholder || (isRTL ? 'اختر المدير' : 'Select manager');
    }
    return `${selected.first_name || ''} ${selected.last_name || ''}`.trim() || selected.id;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            'w-full justify-between bg-card text-start font-normal',
            !value && 'text-muted-foreground',
            'hover:bg-card hover:border-brand-orange/50'
          )}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate" title={triggerLabel}>{triggerLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] p-0 overflow-hidden"
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue is the searchable haystack we set on each CommandItem
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <CommandInput
              placeholder={
                searchPlaceholder ||
                (isRTL ? 'ابحث بالاسم، القسم، أو الوظيفة...' : 'Search by name, department, or title...')
              }
              className="ps-9"
              data-testid={`${testId}-search`}
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>
              {emptyText || (isRTL ? 'لا توجد نتائج' : 'No results')}
            </CommandEmpty>
            <CommandGroup>
              {/* None option */}
              <CommandItem
                value="__none__ none --"
                onSelect={() => {
                  onChange('');
                  setOpen(false);
                }}
                data-testid={`${testId}-option-none`}
                className="text-muted-foreground"
              >
                <X className="me-2 h-4 w-4" />
                {noneLabel || (isRTL ? '— لا يوجد —' : '— None —')}
                {!value && <Check className="ms-auto h-4 w-4 text-brand-orange" />}
              </CommandItem>

              {sortedEmployees.map((emp) => {
                const enName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                const arName = `${emp.first_name_ar || ''} ${emp.last_name_ar || ''}`.trim();
                const dept = emp.department || '';
                const job = emp.job_title || '';
                // Build a haystack that the cmdk filter searches against
                const haystack = [enName, arName, dept, job, emp.id].filter(Boolean).join(' ');
                const isActive = emp.id === value;

                return (
                  <CommandItem
                    key={emp.id}
                    value={haystack}
                    onSelect={() => {
                      onChange(emp.id);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-option-${emp.id}`}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2 flex-1 min-w-0">
                      <span className="text-sm font-medium truncate">
                        {enName || emp.id}
                      </span>
                      {isActive && <Check className="h-4 w-4 text-brand-orange shrink-0" />}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ManagerSelect;
